import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../services/supabase';
import { PageHeader, SearchBar, Card, Table, Td, EmptyRow, Btn, Badge, CompanyInit, StatCard, useToast, Modal, FormField, Input, Select } from '../components/ui';

export function Users() {
  const toast = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [createdUser, setCreatedUser] = useState<any>(null);
  const [form, setForm] = useState({ full_name: '', email: '', role: 'company_admin', company_id: '', employee_code: '', kiosk_only: 'false' });

  const load = useCallback(async () => {
    const [{ data: u }, { data: c }] = await Promise.all([
      sb.from('users').select('*, company:companies(name)').order('created_at', { ascending: false }).limit(200),
      sb.from('companies').select('id, name').eq('is_active', true).order('name'),
    ]);
    setUsers(u || []); setCompanies(c || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => u.full_name?.toLowerCase().includes(query.toLowerCase()) || u.email?.toLowerCase().includes(query.toLowerCase()));

  const createUser = async () => {
    if (!form.full_name || (!form.email && form.kiosk_only === 'false')) { toast('Name and email required', 'error'); return; }
    setSaving(true);
    try {
      if (form.kiosk_only === 'true') {
        const { error } = await sb.from('users').insert({ full_name: form.full_name, role: form.role, company_id: form.company_id || null, employee_code: form.employee_code || null, kiosk_only: true, is_active: true, face_enrolled: false });
        if (error) throw error;
        setCreatedUser({ full_name: form.full_name, email: 'kiosk-only', temp_password: '' });
      } else {
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch('https://quxasqjlhohghnuurvvl.supabase.co/functions/v1/create-user', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ ...form, kiosk_only: false }),
        });
        const result = await res.json();
        if (!result.success) throw new Error(result.error);
        setCreatedUser({ full_name: form.full_name, email: result.email, temp_password: result.temp_password });
      }
      toast(`✅ ${form.full_name} created!`, 'success'); setModal(false); load();
    } catch (e: any) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="All Users" subtitle={`${users.length} total`} action={<Btn onClick={() => setModal(true)}>+ Add User</Btn>} />
      <SearchBar placeholder="Search by name or email..." onSearch={setQuery} />
      <Card>
        <Table headers={['User', 'Role', 'Company', 'Face', 'Kiosk Only', 'Status', 'Created']}>
          {filtered.length === 0 ? <EmptyRow cols={7} /> : filtered.map(u => (
            <tr key={u.id}>
              <Td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><CompanyInit name={u.full_name || '?'} size={32} /><div><div style={{ fontWeight: 600 }}>{u.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.email || 'No email'}</div></div></div></Td>
              <Td><Badge type={u.role === 'platform_admin' ? 'yellow' : u.role === 'company_admin' ? 'purple' : 'green'}>{u.role}</Badge></Td>
              <Td>{u.company?.name || <span style={{ color: 'var(--muted)' }}>—</span>}</Td>
              <Td><Badge type={u.face_enrolled ? 'green' : 'red'}>{u.face_enrolled ? 'Yes' : 'No'}</Badge></Td>
              <Td><Badge type={u.kiosk_only ? 'purple' : 'green'}>{u.kiosk_only ? 'Kiosk' : 'Mobile'}</Badge></Td>
              <Td><Badge type={u.is_active ? 'green' : 'red'}>{u.is_active !== false ? 'active' : 'inactive'}</Badge></Td>
              <Td mono>{new Date(u.created_at).toLocaleDateString('en-IN')}</Td>
            </tr>
          ))}
        </Table>
      </Card>

      {modal && (
        <Modal title="Add User" onClose={() => setModal(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><FormField label="Full Name"><Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="John Doe" /></FormField></div>
            <FormField label="Role">
              <Select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
                <option value="platform_admin">Platform Admin</option><option value="company_admin">Company Admin</option>
                <option value="manager">Manager</option><option value="employee">Employee</option>
              </Select>
            </FormField>
            <FormField label="Company">
              <Select value={form.company_id} onChange={e => setForm(f => ({...f, company_id: e.target.value}))}>
                <option value="">— Select Company —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormField>
            <FormField label="User Type">
              <Select value={form.kiosk_only} onChange={e => setForm(f => ({...f, kiosk_only: e.target.value}))}>
                <option value="false">📱 Mobile App User</option><option value="true">📟 Kiosk Only</option>
              </Select>
            </FormField>
            {form.kiosk_only === 'false' && <FormField label="Email"><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="john@company.com" /></FormField>}
            <FormField label="Employee Code"><Input value={form.employee_code} onChange={e => setForm(f => ({...f, employee_code: e.target.value}))} placeholder="EMP001" /></FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn onClick={createUser} disabled={saving}>{saving ? 'Creating...' : 'Create User'}</Btn>
          </div>
        </Modal>
      )}

      {createdUser && (
        <Modal title="✅ User Created!" onClose={() => setCreatedUser(null)}>
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{createdUser.full_name}</div>
          </div>
          {createdUser.email !== 'kiosk-only' ? (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>Email</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 13 }}>{createdUser.email}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>Temp Password</span>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--warn)', background: '#F9A82515', padding: '4px 10px', borderRadius: 6 }}>{createdUser.temp_password}</span>
              </div>
            </div>
          ) : (
            <div style={{ background: '#845EC210', border: '1px solid #845EC230', borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 13, color: '#845EC2' }}>
              📟 Kiosk-only employee created. Enroll face on the kiosk tablet.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setCreatedUser(null)}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Billing() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [stats, setStats] = useState({ mrr: 0, total_employees: 0 });

  useEffect(() => {
    (async () => {
      const { data } = await sb.from('companies').select('*, users(count)').eq('is_active', true);
      const c = data || [];
      const priceMap: Record<string, number> = { starter: 40, growth: 30, enterprise: 20, trial: 0 };
      let mrr = 0, totalEmp = 0;
      c.forEach((co: any) => {
        const empCount = co.users?.[0]?.count || 0;
        totalEmp += empCount;
        mrr += empCount * (priceMap[co.plan] || 0);
      });
      setCompanies(c);
      setStats({ mrr, total_employees: totalEmp });
    })();
  }, []);

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Billing & Plans" subtitle="Revenue and subscription overview" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 32 }}>
        <StatCard label="Monthly Revenue (Est.)" value={`₹${stats.mrr.toLocaleString('en-IN')}`} color="var(--accent)" />
        <StatCard label="Total Employees Billed" value={stats.total_employees} color="#845EC2" />
        <StatCard label="Active Companies" value={companies.length} color="var(--warn)" />
      </div>
      <Card>
        <Table headers={['Company', 'Plan', 'Price/Emp', 'Features', 'Est. MRR']}>
          {companies.map((c: any) => {
            const priceMap: Record<string, number> = { starter: 40, growth: 30, enterprise: 20, trial: 0 };
            const price = priceMap[c.plan] || 0;
            const empCount = c.users?.[0]?.count || 0;
            return (
              <tr key={c.id}>
                <Td><div style={{ fontWeight: 600 }}>{c.name}</div></Td>
                <Td><Badge type={c.plan || 'trial'}>{c.plan || 'trial'}</Badge></Td>
                <Td mono>₹{price}/emp</Td>
                <Td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {c.feature_attendance !== false && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#00C9A720', color: 'var(--accent)' }}>Attend</span>}
                    {c.feature_leave && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#845EC220', color: '#845EC2' }}>Leave</span>}
                    {c.feature_payroll && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#F9A82520', color: 'var(--warn)' }}>Payroll</span>}
                  </div>
                </Td>
                <Td mono><span style={{ color: 'var(--accent)', fontWeight: 700 }}>₹{(empCount * price).toLocaleString('en-IN')}</span></Td>
              </tr>
            );
          })}
        </Table>
      </Card>
    </div>
  );
}

export function OtherPages() {
  return (
    <div style={{ padding: 32 }}>
      <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--muted)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🚧</div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>Coming Soon</div>
        <div>This section is under development</div>
      </div>
    </div>
  );
}
